import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const redirect = loc.state?.from || "/journal";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.email, form.password, form.name || form.email.split("@")[0]);
      toast.success("Welcome to The Shift.");
      nav(redirect);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 sm:pt-24 min-h-screen aurora grain flex items-center" data-testid="auth-page">
      <div className="mx-auto w-full max-w-md px-5 sm:px-6 py-10 sm:py-16">
        <div className="text-center mb-8 sm:mb-10">
          <Sparkles className="h-6 w-6 text-accent mx-auto mb-4" />
          <div className="eyebrow mb-2">The Shift</div>
          <h1 className="font-serif text-2xl sm:text-4xl leading-tight">
            {mode === "login" ? "Return to your practice." : "Begin your becoming."}
          </h1>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="eyebrow block mb-2">Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="How the room should address you"
                className="h-12"
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div>
            <label className="eyebrow block mb-2">Email</label>
            <Input
              type="email"
              autoComplete="email"
              inputMode="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@shifting.com"
              className="h-12"
              data-testid="auth-email-input"
            />
          </div>
          <div>
            <label className="eyebrow block mb-2">Password</label>
            <Input
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="At least 6 characters"
              className="h-12"
              data-testid="auth-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full btn-pill font-mono uppercase text-xs tracking-widest h-12"
            data-testid="auth-submit-btn"
          >
            {loading ? "..." : mode === "login" ? "Enter" : "Create account"}
          </Button>
        </form>

        <div className="mt-6 sm:mt-8 text-center">
          <button
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-accent py-3 px-4 min-h-[44px]"
            data-testid="auth-toggle-mode"
          >
            {mode === "login" ? "No account yet? Create one" : "Already have an account? Sign in"}
          </button>
        </div>

        <div className="mt-10 sm:mt-12 text-center">
          <Link to="/" className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground hover:text-foreground">
            ← Back home
          </Link>
        </div>
      </div>
    </div>
  );
}
